
import SEOMetadata from '@/components/Common/SEOMetadata';
import AutocompleteFormField from '@components/Common/AutocompleteFormField';
import { SectionCard } from '@components/Common/EntityWorkspace';
import { PageHero } from '@components/Common/Panel';
import ErrorMessage from '@components/Common/ErrorMessage';
import FormField from '@components/Common/FormField';
import Loader from '@components/Common/Loader';
import LocationAutocomplete, { AddressDetails } from '@components/Common/LocationAutocomplete';
import PhoneInput from '@components/Common/PhoneInput';
import SelectFormField from '@components/Common/SelectFormField';
import { TextEditor } from '@components/Common/TextEditor/TextEditor';
import {
    HIRING_MODE_OPTIONS,
    MANAGED,
    MANAGED_WITHOUT_RECRUITER_WARNING,
} from '@components/Common/Utils/hiringMode';
import { isSuperAdmin, useCurrentUserRole } from '@components/Common/Utils/roles';
import ToggleField from '@components/Common/ToggleField';
import { createJobData, getActiveValuesByTypeQuery } from '@constants/graphQLQueries';
import { FALLBACK_CATEGORIES, FALLBACK_INDUSTRIES, FALLBACK_SKILLS } from '@constants/jobMetadataConstants';
import { REQUIREMENT_STATUSES } from '@constants/applicationStatus';
import { EXPERIENCE_LEVELS as EXPERIENCE_LEVEL_OPTIONS, JOB_STATUSES, JOB_TYPES } from '@constants/jobEnums';
import { SALARY_CURRENCIES, SALARY_FREQUENCIES } from '@constants/salary';
import { fetchGraphQLApiData } from '@helpers/graphQLApi';
import {
    Box,
    Button,
    Chip,
    ToggleButton,
    ToggleButtonGroup,
    Typography
} from '@mui/material';
import axios from 'axios';
import debounce from 'lodash.debounce';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { JobFormData } from '../types';
import './index.scss';
import { validateJobForm } from './validateJobForm';

/** The four stored values, as plain strings: this Autocomplete takes `string[]`. */
const EXPERIENCE_LEVELS = EXPERIENCE_LEVEL_OPTIONS.map((o) => o.value);

/**
 * The required fields, in the order they appear on screen, paired with the id of the control to
 * focus. `city`/`state`/`country` all resolve to the one address search that writes them: there is
 * no separate input for any of the three, which is precisely why their errors had nowhere to go.
 */
const FIELD_FOCUS_ORDER: { key: string; id: string }[] = [
    { key: 'title', id: 'title' },
    { key: 'companyId', id: 'companyId' },
    { key: 'jobType', id: 'jobType' },
    { key: 'category', id: 'category' },
    { key: 'industry', id: 'industry' },
    { key: 'city', id: 'addressSearch' },
    { key: 'state', id: 'addressSearch' },
    { key: 'country', id: 'addressSearch' },
    { key: 'salaryMin', id: 'salaryMin' },
    { key: 'salaryMax', id: 'salaryMax' },
    { key: 'description', id: 'job-description-field' },
];

/** What the summary calls each field. The validator's own strings name keys, not labels. */
const FIELD_LABELS: { [key: string]: string } = {
    title: 'Job Title',
    companyId: 'Company',
    jobType: 'Job Type',
    category: 'Category',
    industry: 'Industry',
    city: 'City',
    state: 'State',
    country: 'Country',
    salaryMin: 'Min Salary',
    salaryMax: 'Max Salary',
    description: 'Description',
};

const defaultFormData: JobFormData = {
    id: '',
    title: '',
    description: '',
    jobType: '',
    jobStatus: 'OPEN',
    industry: '',
    category: '',
    location: '',
    city: '',
    state: '',
    country: '',
    zipCode: '',
    salary: '',
    salaryCurrency: '',
    salaryAmount: '',
    salaryMin: null,
    salaryMax: null,
    salaryFrequency: '',
    ageLimit: '',
    gender: '',
    expiryDate: '',
    emailAddress: '',
    phoneNumber: '',
    summary: '',
    isFeatured: false,
    isVerified: false,
    postType: '',
    seoPosting: false,
    sponsored: '',
    jobCode: '',
    experience: '',
    yearsOfExperience: 0,
    education: '',
    skillsRequired: [],
    mustHaveSkills: [],
    niceToHaveSkills: [],
    responsibilities: '',
    tags: [],
    isRemote: false,
    metadata: {
        title: '',
        url: '',
        description: '',
        keywords: '',
        metadata: [],
    },
    isExternalJob: false,
    jobUrl: '',
    jobSource: '',
    companyName: '',
    requirements: '',
    createdAt: '',
    createdBy: '',
    updatedAt: '',
    updatedBy: '',
    companyId: null,
    employerId: null,
    employerEmail: '',
    embeddings: null,
    locality: '',
    placeId: '',
    lat: null,
    lng: null,
    salesOwnerName: '',
    salesOwnerId: '',
    numberOfPositions: null,
    internalFilters: [],
};

/*
 * Job type, status, currency and pay frequency all come from the shared constants now. Each of
 * these was declared here AND again in JobWorkspace, and they had drifted: this list was missing
 * INTERNSHIP (so an internship could not be created here at all) while the workspace's copy used
 * display strings that the schema rejects outright.
 *
 * EXPIRED is filtered out of the status options on purpose. It is a legal JobStatus and the
 * workspace offers it, but a job that is expired the moment it is created is not a state anyone
 * means to choose.
 */
const jobStatusOptions = JOB_STATUSES.filter((o) => o.value !== 'EXPIRED');

const JobCreatePopup: React.FC = () => {

    const [formData, setFormData] = useState<JobFormData>(defaultFormData);

    /*
     * Managed hiring is Super-Admin only, matching the card on the job workspace. Both fields
     * decide who can see a job's candidates, so an HR who could set them could grant themselves an
     * account or take one from a colleague. assignJobHr is Super-Admin gated on the BFF too;
     * hiringMode rides updateJobById/createJob, which are not, so this half is a UI gate.
     */
    const canManageHiring = isSuperAdmin(useCurrentUserRole());
    const [hrUsers, setHrUsers] = useState<Array<{ _id: string; name: string }>>([]);

    useEffect(() => {
        if (!canManageHiring) return;
        let cancelled = false;
        axios
            .get('/api/users?page=0&size=200')
            .then((r) => {
                if (cancelled) return;
                const list: any[] = r?.data?.content ?? r?.data ?? [];
                setHrUsers(
                    list
                        .filter((u) => u?.role === 'HR')
                        .map((u) => ({
                            _id: u._id,
                            name:
                                u?.personalDetails?.fullName ||
                                [u?.personalDetails?.firstName, u?.personalDetails?.lastName]
                                    .filter(Boolean)
                                    .join(' ') ||
                                u._id,
                        })),
                );
            })
            .catch(() => {
                /* the directory is a convenience; the form still saves without it */
            });
        return () => {
            cancelled = true;
        };
    }, [canManageHiring]);
    const [salaryMode, setSalaryMode] = useState<'confidential' | 'range'>('confidential');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [categoryOptions, setCategoryOptions] = useState<string[]>(FALLBACK_CATEGORIES);
    const [industryOptions, setIndustryOptions] = useState<string[]>(FALLBACK_INDUSTRIES);
    const [skillOptions, setSkillOptions] = useState<string[]>(FALLBACK_SKILLS);
    /*
     * The chip editors need real input state. Both fields used to pass a literal `inputValue=""`
     * with a no-op `onInputChange`, which locks MUI's Autocomplete into controlled mode: every
     * keystroke was discarded, and because the filter ran against the pinned empty string the
     * dropdown never narrowed. Clicking an option was the only way to add anything.
     */
    const [skillInput, setSkillInput] = useState('');
    const [tagInput, setTagInput] = useState('');
    const navigate = useNavigate();

    const handleAddressChange = (details: AddressDetails) => {
        setFormData(prev => ({
            ...prev,
            address: details.address,
            locality: details.locality,
            city: details.city,
            state: details.state,
            country: details.country,
            zipCode: details.zipCode,
            placeId: details.placeId,
            lat: details.lat,
            lng: details.lng,
            location: [details.city, details.state, details.country].filter(Boolean).join(', '),
        }));
    };

    const handleChange = (name: string, value: any) => {
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleRemoveSkill = (index: number) => {
        const skills = [...formData.skillsRequired];
        skills.splice(index, 1);
        setFormData((prev) => ({ ...prev, skillsRequired: skills }));
    };

    const addSkill = (raw: string) => {
        const value = (raw || '').trim();
        if (!value) return;
        setFormData(prev =>
            prev.skillsRequired.includes(value)
                ? prev
                : { ...prev, skillsRequired: [...prev.skillsRequired, value] },
        );
        setSkillInput('');
    };

    const addTag = (raw: string) => {
        const value = (raw || '').trim();
        if (!value) return;
        setFormData(prev =>
            prev.tags.includes(value) ? prev : { ...prev, tags: [...prev.tags, value] },
        );
        setTagInput('');
    };

    const handleRemoveTag = (index: number) => {
        const tags = [...formData.tags];
        tags.splice(index, 1);
        setFormData((prev) => ({ ...prev, tags: tags }));
    };


    /** Runs the pure validator in ./validateJobForm and puts its result on screen. */
    const runValidation = (data: JobFormData) => {
        const tempErrors = validateJobForm(data, salaryMode);
        setErrors(tempErrors);
        return tempErrors;
    };

    /**
     * Move the user to the first field that failed, and say what is missing.
     *
     * This is the whole of the "Save does nothing" bug. `handleSubmit` was a bare
     * `if (validateJobForm(...)) { ...submit... }` with no `else`, so a failed validation returned
     * having done nothing: no toast, no message, no request. Four of the nine required fields made
     * that total — `city`, `state` and `country` had no error UI anywhere in this file, and
     * `description`'s sits far down the page. A job for an address with no state (Phnom Penh, for
     * one) was unpostable with nothing on screen to say why.
     *
     * `focus()`, never `scrollIntoView`: the field that is wrong should be the one taking the next
     * keystroke.
     */
    const focusFirstInvalid = (tempErrors: { [key: string]: string }) => {
        for (const { key, id } of FIELD_FOCUS_ORDER) {
            if (!tempErrors[key]) continue;
            const targetId =
                key === 'companyId' && formData.isExternalJob ? 'companyName' : id;
            // The description is a contenteditable with no id of its own, so reach it through its
            // wrapper rather than leaving the one rich-text field unreachable.
            const el =
                document.getElementById(targetId) ??
                document.querySelector<HTMLElement>(`#${targetId} [contenteditable]`);
            if (el) {
                el.focus();
                return;
            }
        }
    };

    const handleSubmit = async () => {
        const tempErrors = runValidation(formData);
        const missing = Object.keys(tempErrors);
        if (missing.length > 0) {
            const labels = missing.map((k) => FIELD_LABELS[k] ?? k);
            // One problem: say it in the field's own words, which for the address and the salary
            // range is an instruction ("Pick a state ...") rather than "<Field> is required".
            const only = tempErrors[missing[0]];
            setError(
                labels.length === 1
                    ? `${only}${only.endsWith('.') ? '' : '.'}`
                    : `Fill in ${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}.`,
            );
            focusFirstInvalid(tempErrors);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const query = createJobData(formData);
            await fetchGraphQLApiData(query, { showStatus: true, message: 'Job created' });
            navigate('/jobs');
        } catch (err) {
            setError('Failed to create job. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const setTextEditorValue = (value: string) => {
        setFormData((prev) => ({ ...prev, description: value }));
    };

    /*
     * Requirements and responsibilities are rich text, because the job page renders them as rich
     * text.
     *
     * They were plain textareas here and TextEditor on the edit screen, which was reported as a
     * missing toolbar. The toolbar is the smaller half. JobWorkspace and the public posting both
     * render these two fields with dangerouslySetInnerHTML, so a newline typed into a textarea is
     * stored as a newline and HTML collapses it: every list an operator typed here came back as one
     * run-on paragraph the next time the job was opened. `description` had the editor on both sides
     * all along, which is why the same form produced one field that survived and two that did not.
     */
    const setRichField = (name: 'requirements' | 'responsibilities') => (value: string) => {
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    // Employers state
    const [employers, setEmployers] = useState<any[]>([]);
    const [employerFilter, setEmployerFilter] = useState<string>('');

    // Fetch employers
    const fetchEmployers = useCallback(async (filter: string) => {
        try {
            if (filter) {
                const filters = filter ? `[{ key: "personalDetails.email", value: "${filter}" }]` : '';
                const query = `
        query {
          getAllEmployers(page: 0, size: 100, filters: ${filters}) {
            content {
              _id
              personalDetails { email }
            }
          }
        }
      `;
                const response = await fetchGraphQLApiData(query);
                if (response?.data?.getAllEmployers) {
                    setEmployers(response.data.getAllEmployers.content || []);
                } else {
                    setEmployers([]);
                }
            }
        } catch (error) {
            setEmployers([]);
        }
    }, []);

    // Debounced fetch for employers
    const debouncedFetchEmployers = useCallback(
        debounce((filter: string) => {
            fetchEmployers(filter);
        }, 400),
        [fetchEmployers]
    );

    useEffect(() => {
        fetchEmployers('');
        return () => {
            debouncedFetchEmployers.cancel();
        };
    }, [fetchEmployers]);

    // Fetch company data
    const [companies, setCompanies] = useState<any[]>([]);
    const [companyFilter, setCompanyFilter] = useState<string>('');

    const fetchCompanies = useCallback(async (filter: string) => {
        try {
            if (filter) {
                const filters = filter ? `[{ key: "companyName", value: "${filter}" }]` : '';
                const query = `
            query {
              getAllCompanies(page: 0, size: 100, filters: ${filters}) {
                content {
                  _id
                  companyName
                  logoUrl
                }
              }
            }
          `;
                const response = await fetchGraphQLApiData(query);
                if (response?.data?.getAllCompanies) {
                    setCompanies(response.data.getAllCompanies.content || []);
                } else {
                    setCompanies([]);
                    console.error('Error fetching companies: Invalid response', response);
                }
            }
        } catch (error) {
            console.error('Error fetching companies:', error);
            setCompanies([]);
        } finally {
            setLoading(false);
        }
    }, []);

    // Debounced fetch for companies
    const debouncedFetchCompanies = useCallback(
        debounce((filter: string) => {
            fetchCompanies(filter);
        }, 400),
        [fetchCompanies]
    );

    useEffect(() => {
        fetchCompanies('');
        return () => {
            debouncedFetchCompanies.cancel();
        };
    }, [fetchCompanies]);

    useEffect(() => {
        (async () => {
            try {
                const [catData, indData, skillData] = await Promise.all([
                    fetchGraphQLApiData(getActiveValuesByTypeQuery('CATEGORY')),
                    fetchGraphQLApiData(getActiveValuesByTypeQuery('INDUSTRY')),
                    fetchGraphQLApiData(getActiveValuesByTypeQuery('SKILL')),
                ]);
                const cats: string[] = catData?.data?.getActiveValuesByType ?? [];
                const inds: string[] = indData?.data?.getActiveValuesByType ?? [];
                const skills: string[] = skillData?.data?.getActiveValuesByType ?? [];
                if (cats.length > 0) setCategoryOptions(cats);
                if (inds.length > 0) setIndustryOptions(inds);
                if (skills.length > 0) setSkillOptions(skills);
            } catch {
                // use fallbacks
            }
        })();
    }, []);


    return (
        <Box className="cc-create">
            <PageHero
                kicker="New job"
                title="Post a job to the platform."
                text="Title, company and location are the ones that matter for matching. The rest can follow from the job's workspace."
                actions={(
                    <>
                        <Button variant="outlined" onClick={() => navigate(-1)} disabled={loading}>
                            Cancel
                        </Button>
                        <Button variant="contained" onClick={handleSubmit} disabled={loading}>
                            {loading ? 'Creating…' : 'Create job'}
                        </Button>
                    </>
                )}
            />

            {loading && <Loader />}
            {error && <ErrorMessage message={error} />}
                <Box className="cc-create__cards">
                    {/* Basic Information */}
                    <SectionCard title="Basic Information">
                        <FormField
                            required
                            label="Job Title"
                            name="title"
                            value={formData.title}
                            onChange={handleChange}
                            error={!!errors.title}
                            helperText={errors.title}
                        />
                        <SelectFormField
                          required={false}
                            label="Job Status"
                            name="jobStatus"
                            options={jobStatusOptions}
                            value={formData.jobStatus}
                            onChange={handleChange}
                        />
                        <FormField
                          required={false}
                            label="Expiry Date"
                            name="expiryDate"
                            value={formData.expiryDate}
                            type="date"
                            onChange={handleChange}
                        />
                        <SelectFormField
                          required={false}
                            label="External Job"
                            name="isExternalJob"
                            options={[
                                { value: 'Yes', label: 'Yes' },
                                { value: 'No', label: 'No' }
                            ]}
                            value={formData.isExternalJob ? 'Yes' : 'No'}
                            onChange={(name, value) => handleChange(name, value === 'Yes')}
                        />
                    </SectionCard>

                    {/* Company & Employer OR External Job Details */}
                    {formData.isExternalJob ? (
                        <SectionCard title="External Job Details">
                            <FormField
                                required
                                label="Company Name"
                                name="companyName"
                                value={formData.companyName}
                                onChange={handleChange}
                                error={!!errors.companyId}
                                helperText={errors.companyId}
                            />
                            <FormField
                              required={false}
                                fullWidth
                                className="text-field"
                                label="Job Code"
                                name="jobCode"
                                formData={formData}
                                onChange={handleChange}
                            />
                            <FormField
                              required={false}
                                fullWidth
                                className="text-field"
                                label="Job Source"
                                name="jobSource"
                                formData={formData}
                                onChange={handleChange}
                            />
                            <FormField
                              required={false}
                                fullWidth
                                className="text-field"
                                label="Job URL"
                                name="jobUrl"
                                formData={formData}
                                onChange={handleChange}
                            />
                        </SectionCard>
                    ) : (
                        <SectionCard title="Company & Employer">
                            <Box className="cc-field--full">
                                <AutocompleteFormField
                                    fullWidth
                                    autocompleteProps={{
                                        isOptionEqualToValue: (option: any, value: any) => option._id === value._id
                                    }}
                                    className="text-field"
                                    error={!!errors.companyId}
                                    helperText={errors.companyId}
                                    getOptionLabel={(option) => option.companyName || ''}
                                    inputValue={companyFilter}
                                    required
                                    label="Company"
                                    name="companyId"
                                    options={companies}
                                    textFieldProps={{
                                        onKeyDown: e => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                            }
                                        }
                                    }}
                                    value={companies.find(c => c._id === formData.companyId) || null}
                                    onChange={(_, newValue) => {
                                        if (newValue && newValue._id !== '' && newValue._id !== formData.companyId) {
                                            handleChange('companyId', newValue._id);
                                            handleChange('companyName', newValue.companyName);
                                            setCompanyFilter(newValue.companyName || '');
                                        }
                                    }}
                                    onInputChange={(_, newInputValue, reason) => {
                                        if (reason === 'input') {
                                            setCompanyFilter(newInputValue);
                                            debouncedFetchCompanies(newInputValue);
                                        }
                                    }}
                                />
                            </Box>
                            <Box className="cc-field--full">
                                <AutocompleteFormField
                                  required={false}
                                    getOptionLabel={(option) => option.personalDetails?.email || ''}
                                    inputValue={formData.employerEmail || employerFilter}
                                    label="Employer"
                                    name="employerEmail"
                                    options={employers}
                                    textFieldProps={{
                                        error: !!errors.employerId || !!errors.employerEmail,
                                        helperText: errors.employerId || errors.employerEmail,
                                        onKeyDown: e => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                            }
                                        }
                                    }}
                                    value={employers.find(e => e._id === formData.employerId) || null}
                                    onChange={(_, newValue) => {
                                        if (newValue && newValue._id !== '' && newValue._id !== formData.employerId) {
                                            handleChange('employerId', newValue._id);
                                            handleChange('employerEmail', newValue.personalDetails?.email || '');
                                            setEmployerFilter(newValue.personalDetails?.email || '');
                                        }
                                    }}
                                    onInputChange={(_, newInputValue, reason) => {
                                        if (reason === 'input') {
                                            setEmployerFilter(newInputValue);
                                            debouncedFetchEmployers(newInputValue);
                                            handleChange('employerEmail', newInputValue);
                                        }
                                    }}
                                />
                            </Box>
                        </SectionCard>
                    )}

                    {/* Classification */}
                    <SectionCard title="Classification">
                        <Box className="cc-field--full">
                            <SelectFormField
                                required
                                label="Job Type"
                                name="jobType"
                                options={JOB_TYPES}
                                value={formData.jobType}
                                onChange={handleChange}
                                error={!!errors.jobType}
                                helperText={errors.jobType}
                            />
                        </Box>
                        <Box className="cc-field--full">
                            <AutocompleteFormField
                                className="text-field"
                                error={!!errors.category}
                                helperText={errors.category}
                                inputValue={formData.category}
                                required
                                label="Category"
                                name="category"
                                options={categoryOptions}
                                value={formData.category}
                                onChange={(_event, newValue) => {
                                    setFormData({ ...formData, category: newValue || '' });
                                }}
                                onInputChange={(_event, newInputValue) => {
                                    setFormData({ ...formData, category: newInputValue });
                                }}
                            />
                        </Box>
                        <Box className="cc-field--full">
                            <AutocompleteFormField
                                className="text-field"
                                inputValue={formData.industry}
                                error={!!errors.industry}
                                helperText={errors.industry}
                                required
                                label="Industry"
                                name="industry"
                                options={industryOptions}
                                value={formData.industry}
                                onChange={(_event, newValue) => {
                                    setFormData({ ...formData, industry: newValue || '' });
                                }}
                                onInputChange={(_event, newInputValue) => {
                                    setFormData({ ...formData, industry: newInputValue });
                                }}
                            />
                        </Box>
                    </SectionCard>

                    {/*
                      * Managed hiring. Super Admins only.
                      *
                      * Both fields were absent here while createJob has accepted them all along,
                      * so a managed role could only be made by saving the job and reopening it on
                      * the workspace. Leaving the mode unset is a real choice and the default:
                      * createJob derives it from the employer's isPremium flag, and sending
                      * SELF_SERVE from a control nobody touched would silently override that.
                      */}
                    {canManageHiring && (
                        <SectionCard title="Premium Hiring">
                            <Box className="cc-field--full">
                                <SelectFormField
                                    required={false}
                                    label="Hiring mode"
                                    name="hiringMode"
                                    options={[
                                        { value: '', label: 'Decide from the employer (default)' },
                                        ...HIRING_MODE_OPTIONS,
                                    ]}
                                    value={formData.hiringMode ?? ''}
                                    onChange={(_n: string, v: unknown) =>
                                        setFormData({ ...formData, hiringMode: String(v ?? '') })
                                    }
                                />
                            </Box>
                            {formData.hiringMode === MANAGED && (
                                <Box className="cc-field--full">
                                    <SelectFormField
                                        required={false}
                                        label="Recruiter"
                                        name="assignedHrId"
                                        options={[
                                            { value: '', label: 'Unassigned' },
                                            ...hrUsers.map((u) => ({ value: u._id, label: u.name })),
                                        ]}
                                        value={formData.assignedHrId ?? ''}
                                        onChange={(_n: string, v: unknown) =>
                                            setFormData({
                                                ...formData,
                                                assignedHrId: String(v ?? '') || null,
                                            })
                                        }
                                    />
                                </Box>
                            )}
                            {formData.hiringMode === MANAGED && !formData.assignedHrId && (
                                <Box className="cc-field--full">
                                    <Typography className="cc-jobcreate__warn">
                                        {MANAGED_WITHOUT_RECRUITER_WARNING}
                                    </Typography>
                                </Box>
                            )}
                        </SectionCard>
                    )}

                    {/* Location */}
                    <SectionCard title="Location">
                        <Box className="cc-field--full">
                            <LocationAutocomplete
                                city={formData.city}
                                state={formData.state}
                                country={formData.country}
                                locality={formData.locality}
                                zipCode={formData.zipCode}
                                required
                                error={errors.city || errors.state || errors.country}
                                onChange={handleChange}
                                onAddressChange={handleAddressChange}
                            />
                        </Box>
                    </SectionCard>

                    {/* Compensation & Details */}
                    <SectionCard title="Compensation & Details">
                        <Box className="cc-field--full">
                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', mb: 1 }}>Salary</Typography>
                            <ToggleButtonGroup
                                exclusive
                                size="small"
                                value={salaryMode}
                                onChange={(_, newMode) => {
                                    if (!newMode) return;
                                    setSalaryMode(newMode);
                                    if (newMode === 'confidential') {
                                        setFormData(prev => ({ ...prev, salaryMin: null, salaryMax: null, salaryCurrency: '', salaryFrequency: '' }));
                                    } else {
                                        setFormData(prev => ({ ...prev, salary: '' }));
                                    }
                                }}
                            >
                                <ToggleButton value="confidential">Confidential / As per norms</ToggleButton>
                                <ToggleButton value="range">Specify Range</ToggleButton>
                            </ToggleButtonGroup>
                        </Box>
                        {salaryMode === 'confidential' ? (
                            <FormField
                              required={false}
                                fullWidth
                                className="text-field"
                                error={!!errors.salary}
                                helperText={errors.salary}
                                label="Salary Description (optional)"
                                name="salary"
                                placeholder="e.g. As per industry standards, ₹15-20 LPA, Competitive"
                                value={formData.salary}
                                onChange={handleChange}
                            />
                        ) : (
                            <>
                                <FormField
                                  required
                                    fullWidth
                                    className="text-field"
                                    label="Min Salary"
                                    name="salaryMin"
                                    type="number"
                                    value={formData.salaryMin ?? ''}
                                    error={!!errors.salaryMin}
                                    helperText={errors.salaryMin || 'Full amount, e.g. 500000 for ₹5L p.a.'}
                                    textFieldProps={{ slotProps: { htmlInput: { min: 0 } } }}
                                    onChange={(name, value) => setFormData(prev => ({ ...prev, [name]: value === '' ? null : Number(value) }))}
                                />
                                <FormField
                                  required
                                    fullWidth
                                    className="text-field"
                                    label="Max Salary"
                                    name="salaryMax"
                                    type="number"
                                    value={formData.salaryMax ?? ''}
                                    error={!!errors.salaryMax}
                                    helperText={errors.salaryMax || 'Full amount, e.g. 1500000 for ₹15L p.a.'}
                                    textFieldProps={{ slotProps: { htmlInput: { min: 0 } } }}
                                    onChange={(name, value) => setFormData(prev => ({ ...prev, [name]: value === '' ? null : Number(value) }))}
                                />
                                <SelectFormField
                                  required={false}
                                    label="Currency"
                                    name="salaryCurrency"
                                    options={SALARY_CURRENCIES}
                                    value={formData.salaryCurrency}
                                    onChange={(name, value) => setFormData(prev => ({ ...prev, [name]: value }))}
                                />
                                <SelectFormField
                                  required={false}
                                    label="Pay Frequency"
                                    name="salaryFrequency"
                                    options={SALARY_FREQUENCIES}
                                    value={formData.salaryFrequency}
                                    onChange={(name, value) => setFormData(prev => ({ ...prev, [name]: value }))}
                                />
                            </>
                        )}
                        {/*
                          * Selection only. This was `freeSolo` with `onInputChange` writing raw
                          * keystrokes into `experience`, so anything an admin typed was stored --
                          * and `experience` is matched exactly by the public job filter, the
                          * /jobs/experience/[level] landing pages and the analytics grouping. A
                          * job saved as '2-5 years' looked filled in here and appeared in none of
                          * them. The four levels are the whole vocabulary.
                          */}
                        <AutocompleteFormField
                          required={false}
                            fullWidth
                            className="text-field"
                            label="Experience level"
                            name="experience"
                            options={EXPERIENCE_LEVELS}
                            placeholder="Select experience level"
                            value={formData.experience}
                            onChange={(_event, newValue) => {
                                handleChange('experience', newValue || '');
                            }}
                        />
                        <FormField
                          required={false}
                            fullWidth
                            className="text-field"
                            label="Years required (optional)"
                            name="yearsOfExperience"
                            type="number"
                            value={formData.yearsOfExperience}
                            onChange={handleChange}
                            error={!!errors.yearsOfExperience}
                            helperText={errors.yearsOfExperience || 'Used for Google for Jobs structured data. The level above is what the site filters on.'}
                        />
                        <FormField
                          required={false}
                            fullWidth
                            className="text-field"
                            label="Contact Email"
                            name="emailAddress"
                            type="email"
                            value={formData.emailAddress}
                            onChange={handleChange}
                        />
                        <PhoneInput
                          required={false}
                            fullWidth
                            className="text-field"
                            label="Contact Phone"
                            value={formData.phoneNumber}
                            onChange={(val) => handleChange('phoneNumber', val)}
                        />
                        {/*
                          * Switches, not Yes/No dropdowns.
                          *
                          * Two of these were selects with the options "Yes" and "No", which is a
                          * switch wearing three clicks. The other three had no control at all while
                          * the form carried their values, so `isRemote` and `seoPosting` were posted
                          * as false on every job created here and `dataAvailable` was never set.
                          */}
                        <ToggleField
                            label="Featured"
                            checked={!!formData.isFeatured}
                            onChange={(v) => handleChange('isFeatured', v)}
                        />
                        <ToggleField
                            label="Verified"
                            checked={!!formData.isVerified}
                            onChange={(v) => handleChange('isVerified', v)}
                        />
                        <ToggleField
                            label="Remote"
                            checked={!!formData.isRemote}
                            onChange={(v) => handleChange('isRemote', v)}
                        />
                        <ToggleField
                            label="Include in the SEO job feed"
                            checked={!!formData.seoPosting}
                            onChange={(v) => handleChange('seoPosting', v)}
                        />
                        {/*
                          * Tri-state on the record: null means nobody has said, which is not the same
                          * as No. It is left out of the initial form state so an untouched create
                          * sends nothing and the field stays unrecorded; touching it sends an answer,
                          * including an explicit No.
                          */}
                        <ToggleField
                            label="Candidate data available"
                            checked={!!formData.dataAvailable}
                            onChange={(v) => handleChange('dataAvailable', v)}
                        />
                        {/*
                          * Blank on purpose.
                          *
                          * createJob derives this when it is absent: a managed requirement starts New
                          * because the work has not begun, and a self-serve job starts Active because
                          * it goes live as it is posted. Sending "New" from a control nobody touched
                          * would override that rule on every job. Same reasoning as hiringMode, which
                          * createJobData already omits unless chosen.
                          */}
                        <SelectFormField
                          required={false}
                            label="Requirement status"
                            name="requirementStatus"
                            options={[
                                { value: '', label: 'Set it automatically' },
                                ...REQUIREMENT_STATUSES.map((r) => ({ value: r, label: r })),
                            ]}
                            value={formData.requirementStatus ?? ''}
                            onChange={(name, value) =>
                                handleChange(name, value === '' ? undefined : value)
                            }
                        />
                    </SectionCard>

                    {/* Internal recruiting notes — never shown on the public posting. */}
                    <SectionCard title="Internal recruiting notes">
                        <Typography className="cc-field--full" variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                            Only visible in the Control Center. Never shown on the public job posting.
                        </Typography>
                        <FormField
                          required={false}
                            fullWidth
                            className="text-field"
                            label="Sales / BD owner"
                            name="salesOwnerName"
                            placeholder="Who sourced this demand"
                            value={formData.salesOwnerName}
                            onChange={handleChange}
                        />
                        <FormField
                          required={false}
                            fullWidth
                            type="number"
                            className="text-field"
                            label="Number of openings"
                            name="numberOfPositions"
                            placeholder="e.g. 3"
                            value={
                                formData.numberOfPositions == null
                                    ? ''
                                    : String(formData.numberOfPositions)
                            }
                            onChange={(_name: string, value: any) => {
                                const n =
                                    value === '' || value == null
                                        ? null
                                        : Math.max(1, Number(value) || 1);
                                setFormData((prev) => ({ ...prev, numberOfPositions: n }));
                            }}
                        />
                        <Box className="cc-field--full">
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                Client screening filters (religion, gender preference, locality, etc.)
                            </Typography>
                            {/* Each row is the shared field row. It was a bare `display: flex` with
                                no alignItems, so the default `stretch` drew Remove at the full
                                ~64px height of the labelled fields beside it rather than the
                                input's 36.75px. */}
                            {formData.internalFilters.map((f, idx) => (
                                <Box key={idx} className="cc-field-row cc-field-row--stacked">
                                    <FormField
                                      required={false}
                                        fullWidth
                                        label="Field"
                                        name={`if-key-${idx}`}
                                        placeholder="e.g. Religion, Gender, Locality"
                                        value={f.key}
                                        onChange={(_name: string, value: string) =>
                                            setFormData((prev) => {
                                                const next = [...prev.internalFilters];
                                                next[idx] = { ...next[idx], key: value };
                                                return { ...prev, internalFilters: next };
                                            })
                                        }
                                    />
                                    <FormField
                                      required={false}
                                        fullWidth
                                        label="Requirement"
                                        name={`if-val-${idx}`}
                                        placeholder="e.g. Local to Mumbai"
                                        value={f.value}
                                        onChange={(_name: string, value: string) =>
                                            setFormData((prev) => {
                                                const next = [...prev.internalFilters];
                                                next[idx] = { ...next[idx], value };
                                                return { ...prev, internalFilters: next };
                                            })
                                        }
                                    />
                                    <Button
                                        color="error"
                                        size="small"
                                        aria-label="Remove filter"
                                        onClick={() =>
                                            setFormData((prev) => ({
                                                ...prev,
                                                internalFilters: prev.internalFilters.filter((_, i) => i !== idx),
                                            }))
                                        }
                                    >
                                        Remove
                                    </Button>
                                </Box>
                            ))}
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={() =>
                                    setFormData((prev) => ({
                                        ...prev,
                                        internalFilters: [...prev.internalFilters, { key: '', value: '' }],
                                    }))
                                }
                            >
                                Add filter
                            </Button>
                        </Box>
                    </SectionCard>

                    {/* Skills Required */}
                    <SectionCard title="Skills Required">
                        <Box className="cc-field--full">
                            <Box className="cc-field-row">
                                <AutocompleteFormField
                                  required={false}
                                    freeSolo
                                    fullWidth
                                    className="text-field"
                                    inputValue={skillInput}
                                    label="Add Skill"
                                    name="new-skill"
                                    options={skillOptions.filter(o => !formData.skillsRequired.includes(o))}
                                    placeholder="e.g., Jewellery Design, CAD, Sales..."
                                    textFieldProps={{
                                        onKeyDown: (e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                addSkill(skillInput);
                                            }
                                        }
                                    }}
                                    value={null}
                                    onChange={(_, newValue) => {
                                        addSkill(typeof newValue === 'string' ? newValue : '');
                                    }}
                                    onInputChange={(_, newInputValue) => setSkillInput(newInputValue)}
                                    error={!!errors?.skillsRequired}
                                    helperText={errors?.skillsRequired}
                                />
                            </Box>
                            <Box className="skills-chips-container">
                                {formData.skillsRequired.map((skill, index) => (
                                    skill && skill.trim() !== '' && (
                                        <Chip
                                            key={index}
                                            className="chip-muted"
                                            label={skill}
                                            size="small"
                                            onDelete={() => handleRemoveSkill(index)}
                                        />
                                    )
                                ))}
                            </Box>
                        </Box>
                    </SectionCard>

                    {/* Qualifications */}
                    <SectionCard title="Qualifications">
                        <Box className="cc-field--full">
                            <FormField
                              required={false}
                                multiline
                                errors={errors}
                                formData={formData}
                                label="Education"
                                name="education"
                                rows={3}
                                onChange={handleChange}
                            />
                        </Box>
                        <Box className="cc-field--full">
                            <TextEditor
                                label="Job Requirements"
                                minHeight="12vh"
                                readOnly={false}
                                setValue={setRichField('requirements')}
                                value={formData.requirements}
                            />
                        </Box>
                        <Box className="cc-field--full">
                            <TextEditor
                                label="Responsibilities"
                                minHeight="12vh"
                                readOnly={false}
                                setValue={setRichField('responsibilities')}
                                value={formData.responsibilities}
                            />
                        </Box>
                    </SectionCard>

                    {/* Tags */}
                    <SectionCard title="Tags">
                        <Box className="cc-field--full">
                            <Box className="cc-field-row">
                                <AutocompleteFormField
                                  required={false}
                                    fullWidth
                                    freeSolo
                                    className="text-field"
                                    inputValue={tagInput}
                                    label="Add tags"
                                    name="new-tag"
                                    options={skillOptions.filter(o => !formData.tags.includes(o))}
                                    placeholder="e.g., Jewellery Design, CAD, Sales..."
                                    textFieldProps={{
                                        onKeyDown: (e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                addTag(tagInput);
                                            }
                                        }
                                    }}
                                    value={null}
                                    onChange={(_, newValue) => {
                                        addTag(typeof newValue === 'string' ? newValue : '');
                                    }}
                                    onInputChange={(_, newInputValue) => setTagInput(newInputValue)}
                                    error={!!errors?.tags}
                                    helperText={errors?.tags}
                                />
                            </Box>
                            <Box className="skills-chips-container">
                                {formData.tags.map((tag, index) => (
                                    tag && tag.trim() !== '' && (
                                        <Chip
                                            key={index}
                                            className="chip-muted"
                                            label={tag}
                                            size="small"
                                            onDelete={() => handleRemoveTag(index)}
                                        />
                                    )
                                ))}
                            </Box>
                        </Box>
                    </SectionCard>

                {/* Description and SEO sit in the grid with everything else. They used to be
                    siblings of it, which made them two full-width rows below a two-column form
                    for no reason: the editor is tall and the metadata card is short, so they
                    pair well. */}
                <SectionCard title="Job Description">
                    <Box className="cc-field--full" id="job-description-field">
                        <TextEditor
                            required
                            label="Description"
                            readOnly={false}
                            setValue={setTextEditorValue}
                            value={formData.description}
                        />
                        {!!errors.description && (
                            <Typography color="error" variant="caption">
                                Description is required
                            </Typography>
                        )}
                    </Box>
                </SectionCard>

                {/*
                  * Shown for an external job too, which it was not.
                  *
                  * The two forms disagreed: JobWorkspace edits SEO metadata on every job, this hid
                  * the card whenever External Job was Yes. The site does not make that distinction
                  * either. An external posting renders at /jobs/<slug> like any other, createJob
                  * fills metadata through seoMetadataBuilder.forJob for all of them, and the edit
                  * screen then showed fields the create screen had refused to let anyone author.
                  */}
                <SectionCard title="SEO Metadata">
                    <SEOMetadata
                        data={formData.metadata}
                        onChange={(updatedMetadata) => setFormData(prev => ({ ...prev, metadata: updatedMetadata }))}
                    />
                </SectionCard>
                </Box>

        </Box>
    );
};

export default JobCreatePopup;

